
export const API_BASE_URL_DEFAULT = 'http://localhost:8000' as const;

export const API_V1_PREFIX = '/api/v1' as const;

export function resolveApiUrl(baseUrl: string, path: string): string {
  if (path.startsWith('http')) {
    return path;
  }
  return `${baseUrl.replace(/\/$/, '')}${path}`;
}

export const API_V1_AUTH = {
  csrf: `${API_V1_PREFIX}/auth/csrf/`,
  login: `${API_V1_PREFIX}/auth/login/`,
  logout: `${API_V1_PREFIX}/auth/logout/`,
  signup: `${API_V1_PREFIX}/auth/signup/`,
  verifyEmail: `${API_V1_PREFIX}/auth/verify-email/`,
  me: `${API_V1_PREFIX}/auth/me/`,
  switchOrganization: `${API_V1_PREFIX}/auth/switch-organization/`,
  resendVerification: `${API_V1_PREFIX}/auth/resend-verification/`,
  dismissPostVerifyWelcome: `${API_V1_PREFIX}/auth/dismiss-post-verify-welcome/`,
  guardianPortalInviteContext: `${API_V1_PREFIX}/auth/guardian/invite/`,
  guardianPortalActivate: `${API_V1_PREFIX}/auth/guardian/activate/`,
  workforcePortalInviteContext: `${API_V1_PREFIX}/auth/workforce/invite/`,
  workforcePortalActivate: `${API_V1_PREFIX}/auth/workforce/activate/`,
  workforcePasswordResetContext: `${API_V1_PREFIX}/auth/workforce/password-reset/`,
  workforcePasswordResetComplete: `${API_V1_PREFIX}/auth/workforce/password-reset/complete/`,
} as const;

export const API_V1_ORGANIZATIONS = {
  onboarding: `${API_V1_PREFIX}/organizations/onboarding/`,
  onboardingIdentity: `${API_V1_PREFIX}/organizations/onboarding/identity/`,
  onboardingServiceSites: `${API_V1_PREFIX}/organizations/onboarding/service-sites/`,
  onboardingServiceSitesAdvance: `${API_V1_PREFIX}/organizations/onboarding/service-sites/advance/`,
  onboardingServiceSite: (id: string) =>
    `${API_V1_PREFIX}/organizations/onboarding/service-sites/${encodeURIComponent(id)}/`,
  onboardingTeam: `${API_V1_PREFIX}/organizations/onboarding/team/`,
  onboardingTeamAdvance: `${API_V1_PREFIX}/organizations/onboarding/team/advance/`,
  onboardingTeamMember: (id: string) =>
    `${API_V1_PREFIX}/organizations/onboarding/team/${encodeURIComponent(id)}/`,
  onboardingFirstIntake: `${API_V1_PREFIX}/organizations/onboarding/first-intake/`,
  profile: `${API_V1_PREFIX}/organizations/profile/`,
  profileBranding: `${API_V1_PREFIX}/organizations/profile/branding/`,
  profileLogo: `${API_V1_PREFIX}/organizations/profile/logo/`,
  profileAuditEvents: `${API_V1_PREFIX}/organizations/profile/audit-events/`,
  serviceSites: `${API_V1_PREFIX}/organizations/service-sites/`,
  serviceSite: (id: string) =>
    `${API_V1_PREFIX}/organizations/service-sites/${encodeURIComponent(id)}/`,
  serviceSiteArchive: (id: string) =>
    `${API_V1_PREFIX}/organizations/service-sites/${encodeURIComponent(id)}/archive/`,
  serviceSiteArchiveBlockers: (id: string) =>
    `${API_V1_PREFIX}/organizations/service-sites/${encodeURIComponent(id)}/archive-blockers/`,
  serviceSiteAuditEvents: (id: string) =>
    `${API_V1_PREFIX}/organizations/service-sites/${encodeURIComponent(id)}/audit-events/`,
  dashboardSummary: `${API_V1_PREFIX}/organizations/dashboard/summary/`,
  patientsList: `${API_V1_PREFIX}/organizations/patients/`,
  patientsCareTeamChoices: `${API_V1_PREFIX}/organizations/patients/care-team-choices/`,
  patientsArchivedIntakeMatches: `${API_V1_PREFIX}/organizations/patients/archived-intake-matches/`,
  patientsArchivedList: `${API_V1_PREFIX}/organizations/patients/archived/`,
  patientsBulkImport: `${API_V1_PREFIX}/organizations/patients/bulk-import/`,
  patientsBulkArchive: `${API_V1_PREFIX}/organizations/patients/bulk-archive/`,
  patientsBulkRestore: `${API_V1_PREFIX}/organizations/patients/bulk-restore/`,
  patientsBulkPermanentDelete: `${API_V1_PREFIX}/organizations/patients/bulk-permanent-delete/`,
  patientDetail: (id: string) =>
    `${API_V1_PREFIX}/organizations/patients/${encodeURIComponent(id)}/`,
  patientSendGuardianInvite: (id: string) =>
    `${API_V1_PREFIX}/organizations/patients/${encodeURIComponent(id)}/send-guardian-invite/`,
  patientArchive: (id: string) =>
    `${API_V1_PREFIX}/organizations/patients/${encodeURIComponent(id)}/archive/`,
  patientRestore: (id: string) =>
    `${API_V1_PREFIX}/organizations/patients/${encodeURIComponent(id)}/restore/`,
  patientCarePlans: (patientId: string) =>
    `${API_V1_PREFIX}/organizations/patients/${encodeURIComponent(patientId)}/care-plans/`,
  patientCarePlan: (patientId: string, carePlanId: string) =>
    `${API_V1_PREFIX}/organizations/patients/${encodeURIComponent(patientId)}/care-plans/${encodeURIComponent(carePlanId)}/`,
  patientCarePlanSendForApproval: (patientId: string, carePlanId: string) =>
    `${API_V1_PREFIX}/organizations/patients/${encodeURIComponent(patientId)}/care-plans/${encodeURIComponent(carePlanId)}/send-for-approval/`,
  patientCarePlanSubmitConsent: (patientId: string, carePlanId: string) =>
    `${API_V1_PREFIX}/organizations/patients/${encodeURIComponent(patientId)}/care-plans/${encodeURIComponent(carePlanId)}/submit-consent/`,
  patientCarePlanSendReminder: (patientId: string, carePlanId: string) =>
    `${API_V1_PREFIX}/organizations/patients/${encodeURIComponent(patientId)}/care-plans/${encodeURIComponent(carePlanId)}/send-reminder/`,
  patientUpcomingShifts: (patientId: string) =>
    `${API_V1_PREFIX}/organizations/patients/${encodeURIComponent(patientId)}/upcoming-shifts/`,
  patientReportedIncidents: (patientId: string) =>
    `${API_V1_PREFIX}/organizations/patients/${encodeURIComponent(patientId)}/reported-incidents/`,
  patientReportedIncidentEvidence: (patientId: string, incidentId: string) =>
    `${API_V1_PREFIX}/organizations/patients/${encodeURIComponent(patientId)}/reported-incidents/${encodeURIComponent(incidentId)}/evidence/`,
  patientCarePlanSetupOptions: `${API_V1_PREFIX}/organizations/patients/care-plan-setup-options/`,
  workforceStaffList: `${API_V1_PREFIX}/organizations/workforce/staff/`,
  workforceStaffDetail: (id: string) =>
    `${API_V1_PREFIX}/organizations/workforce/staff/${encodeURIComponent(id)}/`,
  workforceStaffResendInvite: (id: string) =>
    `${API_V1_PREFIX}/organizations/workforce/staff/${encodeURIComponent(id)}/resend-invite/`,
  workforceStaffSiteManagement: (id: string) =>
    `${API_V1_PREFIX}/organizations/workforce/staff/${encodeURIComponent(id)}/site-management/`,
  workforceStaffResetPassword: (id: string) =>
    `${API_V1_PREFIX}/organizations/workforce/staff/${encodeURIComponent(id)}/reset-password/`,
  workforceStaffSendDocumentReminder: (id: string) =>
    `${API_V1_PREFIX}/organizations/workforce/staff/${encodeURIComponent(id)}/send-document-reminder/`,
  workforceStaffProfileImage: (id: string) =>
    `${API_V1_PREFIX}/organizations/workforce/staff/${encodeURIComponent(id)}/profile-image/`,
  workforceStaffDeactivate: `${API_V1_PREFIX}/organizations/workforce/staff/deactivate/`,
  workforceStaffReactivate: `${API_V1_PREFIX}/organizations/workforce/staff/reactivate/`,
  workforceStaffPermanentDelete: `${API_V1_PREFIX}/organizations/workforce/staff/permanent-delete/`,
  workforceNewStaffOptions: `${API_V1_PREFIX}/organizations/workforce/new-staff-options/`,
  workforceNewCareTeamOptions: `${API_V1_PREFIX}/organizations/workforce/new-care-team-options/`,
  workforceCareTeams: `${API_V1_PREFIX}/organizations/workforce/care-teams/`,
  workforceCareTeamsPermanentDelete: `${API_V1_PREFIX}/organizations/workforce/care-teams/permanent-delete/`,
  workforceCareTeamDetail: (id: string) =>
    `${API_V1_PREFIX}/organizations/workforce/care-teams/${encodeURIComponent(id)}/`,
  trainingPrograms: `${API_V1_PREFIX}/organizations/training/programs/`,
  trainingProgramDetail: (id: string) =>
    `${API_V1_PREFIX}/organizations/training/programs/${encodeURIComponent(id)}/`,
  trainingProgramOverview: (id: string) =>
    `${API_V1_PREFIX}/organizations/training/programs/${encodeURIComponent(id)}/overview/`,
  trainingProgramAssignmentOptions: (id: string) =>
    `${API_V1_PREFIX}/organizations/training/programs/${encodeURIComponent(id)}/assignment-options/`,
  trainingProgramAssignments: (id: string) =>
    `${API_V1_PREFIX}/organizations/training/programs/${encodeURIComponent(id)}/assignments/`,
  trainingProgramSendReminders: (id: string) =>
    `${API_V1_PREFIX}/organizations/training/programs/${encodeURIComponent(id)}/send-reminders/`,
  trainingStaffAssignmentOptions: (staffId: string) =>
    `${API_V1_PREFIX}/organizations/training/staff/${encodeURIComponent(staffId)}/assignment-options/`,
  trainingStaffAssignments: (staffId: string) =>
    `${API_V1_PREFIX}/organizations/training/staff/${encodeURIComponent(staffId)}/assignments/`,
  trainingMedia: `${API_V1_PREFIX}/organizations/training/media/`,
  trainingMediaVideo: `${API_V1_PREFIX}/organizations/training/media/video/`,
  trainingMediaVideoInit: `${API_V1_PREFIX}/organizations/training/media/video/init/`,
  trainingMediaVideoComplete: `${API_V1_PREFIX}/organizations/training/media/video/complete/`,
  trainingMediaVideoAbort: `${API_V1_PREFIX}/organizations/training/media/video/abort/`,
  incidentsList: `${API_V1_PREFIX}/organizations/incidents/`,
  incidentsOptions: `${API_V1_PREFIX}/organizations/incidents/options/`,
  incidentDetail: (id: string) =>
    `${API_V1_PREFIX}/organizations/incidents/${encodeURIComponent(id)}/`,
  incidentAttachmentUpload: (id: string) =>
    `${API_V1_PREFIX}/organizations/incidents/${encodeURIComponent(id)}/attachments/`,
  shiftsList: `${API_V1_PREFIX}/organizations/shifts/`,
  shiftsExport: `${API_V1_PREFIX}/organizations/shifts/export/`,
  shiftsOptions: `${API_V1_PREFIX}/organizations/shifts/options/`,
  shiftDetail: (id: string) =>
    `${API_V1_PREFIX}/organizations/shifts/${encodeURIComponent(id)}/`,
  shiftPublish: (id: string) =>
    `${API_V1_PREFIX}/organizations/shifts/${encodeURIComponent(id)}/publish/`,
  shiftCancel: (id: string) =>
    `${API_V1_PREFIX}/organizations/shifts/${encodeURIComponent(id)}/cancel/`,
} as const;
